import { Helmet } from 'react-helmet-async'
import { Language } from '../services/comingSoonTranslations'

interface ComingSoonSEOProps {
  language: Language
}

/**
 * SEO Component for Coming Soon Page
 * 
 * Provides multilingual SEO support (English and Spanish)
 * Updates meta tags, Open Graph, Twitter Cards, and structured data based on language
 */
const ComingSoonSEO: React.FC<ComingSoonSEOProps> = ({ language }) => {
  const isSpanish = language === 'es'

  // English SEO content
  const enContent = {
    title: 'EvoltBay - Coming Soon | Solar Energy Ecommerce | Enterprise Quotation System | Solar Panels Marketplace',
    description: 'EvoltBay is building the next layer of solar commerce. The leading solar energy ecommerce platform and enterprise quotation system for solar panels, solar inverters, solar batteries, and complete solar systems. Enabling auctions, buying & selling, enterprise RFQs, and government contracting for solar manufacturers, distributors, EPCs, and enterprise buyers. Streamline your solar procurement with our advanced quotation system.',
    keywords: 'solar energy, solar energy ecommerce, solar ecommerce, ecommerce, quotation, quotation system, enterprise quotation system, solar panel, solar panels, photovoltaic panels, PV panels, solar inverter, solar inverters, solar battery, solar batteries, battery storage, solar system, solar systems, solar installation, solar installations, solar equipment, solar procurement, solar vendor, solar vendors, solar marketplace, solar auction, solar auctions, solar RFQ, solar RFQs, solar quote, solar quotes, solar contracting, government solar contracting, renewable energy, clean energy, green energy, solar manufacturer, solar manufacturers, solar distributor, solar distributors, solar EPC, EPC solar, solar equipment marketplace, solar panel marketplace, solar marketplace platform, solar procurement platform, enterprise solar procurement, government solar procurement',
    ogTitle: 'EvoltBay - Coming Soon | Solar Energy Ecommerce Platform | Solar Panels Marketplace',
    ogDescription: 'EvoltBay is building the next layer of solar commerce. Leading solar energy ecommerce platform and enterprise quotation system for solar panels, solar inverters, solar batteries, and complete solar systems. Enabling auctions, enterprise RFQs, and government contracting for solar manufacturers, distributors, and EPCs.',
    twitterTitle: 'EvoltBay - Coming Soon | Solar Energy Ecommerce | Solar Panels Marketplace',
    twitterDescription: 'Building the next layer of solar commerce. Leading solar energy ecommerce platform and enterprise quotation system for solar panels, solar equipment, and solar systems. Extending your existing solar ecosystem—not replacing it.'
  }

  // Spanish SEO content
  const esContent = {
    title: 'EvoltBay - Próximamente | Comercio Solar | Sistema de Cotización Empresarial | Mercado de Paneles Solares',
    description: 'EvoltBay está construyendo la próxima capa de comercio solar. La plataforma líder de comercio electrónico de energía solar y sistema de cotización empresarial para paneles solares, inversores solares, baterías solares y sistemas solares completos. Habilitando subastas, compra y venta, RFQs empresariales y contratación gubernamental para fabricantes, distribuidores, EPCs y compradores empresariales solares. Optimiza tu adquisición solar con nuestro sistema de cotización avanzado.',
    keywords: 'energía solar, comercio solar, comercio electrónico solar, ecommerce solar, cotización, sistema de cotización, sistema de cotización empresarial, panel solar, paneles solares, paneles fotovoltaicos, paneles PV, inversor solar, inversores solares, batería solar, baterías solares, almacenamiento de baterías, sistema solar, sistemas solares, instalación solar, instalaciones solares, equipo solar, equipos solares, adquisición solar, proveedor solar, proveedores solares, mercado solar, subasta solar, subastas solares, RFQ solar, RFQs solares, cotización solar, cotizaciones solares, contratación solar, contratación solar gubernamental, energía renovable, energía limpia, energía verde, fabricante solar, fabricantes solares, distribuidor solar, distribuidores solares, EPC solar, mercado de equipos solares, mercado de paneles solares, plataforma de mercado solar, plataforma de adquisición solar, adquisición solar empresarial, adquisición solar gubernamental',
    ogTitle: 'EvoltBay - Próximamente | Plataforma de Comercio Solar | Mercado de Paneles Solares',
    ogDescription: 'EvoltBay está construyendo la próxima capa de comercio solar. Plataforma líder de comercio electrónico de energía solar y sistema de cotización empresarial para paneles solares, inversores solares, baterías solares y sistemas solares completos. Habilitando subastas, RFQs empresariales y contratación gubernamental para fabricantes, distribuidores y EPCs solares.',
    twitterTitle: 'EvoltBay - Próximamente | Comercio Solar | Mercado de Paneles Solares',
    twitterDescription: 'Construyendo la próxima capa de comercio solar. Plataforma líder de comercio electrónico de energía solar y sistema de cotización empresarial para paneles solares, equipos solares y sistemas solares. Extendiendo tu ecosistema solar existente, no reemplazándolo.'
  }

  const content = isSpanish ? esContent : enContent
  const locale = isSpanish ? 'es_US' : 'en_US'
  const langCode = isSpanish ? 'es' : 'en'
  const url = 'https://evoltbay.com/coming-soon'

  // Organization structured data (multilingual)
  const getOrganizationData = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EvoltBay",
    "alternateName": "Evoltbay",
    "url": "https://evoltbay.com",
    "logo": `${typeof window !== 'undefined' ? window.location.origin : 'https://evoltbay.com'}/icons/icon-512x512.png`,
    "description": isSpanish
      ? "Plataforma líder de comercio electrónico de energía solar y sistema de cotización empresarial. Especializada en contratación solar gubernamental, conectando compradores con proveedores certificados para paneles solares, inversores solares, baterías solares y sistemas solares completos. Optimiza tu adquisición solar con nuestro sistema de cotización avanzado."
      : "Leading solar energy ecommerce platform and enterprise quotation system. Specializing in government solar contracting, connecting buyers with certified vendors for solar panels, solar inverters, solar batteries, and complete solar systems. Streamline your solar procurement with our advanced quotation system.",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "support@evoltbay.com",
      "availableLanguage": ["English", "Spanish"]
    },
    "areaServed": {
      "@type": "Country",
      "name": isSpanish ? "Estados Unidos" : "United States"
    },
    "knowsAbout": isSpanish
      ? [
          "Energía Solar",
          "Comercio Solar",
          "Comercio Electrónico Solar",
          "Ecommerce Solar",
          "Cotización",
          "Sistema de Cotización",
          "Sistema de Cotización Empresarial",
          "Contratación Solar Gubernamental",
          "Adquisición de Equipos Solares",
          "Panel Solar",
          "Paneles Solares",
          "Paneles Fotovoltaicos",
          "Paneles PV",
          "Inversor Solar",
          "Inversores Solares",
          "Batería Solar",
          "Baterías Solares",
          "Sistema Solar",
          "Sistemas Solares",
          "Instalación Solar",
          "Equipo Solar",
          "Mercado Solar",
          "Subasta Solar",
          "RFQ Solar",
          "Cotización Solar",
          "Energía Renovable",
          "Energía Limpia",
          "Fabricante Solar",
          "Distribuidor Solar",
          "EPC Solar"
        ]
      : [
          "Solar Energy",
          "Solar Energy Ecommerce",
          "Solar Ecommerce",
          "Ecommerce",
          "Quotation",
          "Quotation System",
          "Enterprise Quotation System",
          "Government Solar Contracting",
          "Solar Equipment Procurement",
          "Solar Panel",
          "Solar Panels",
          "Photovoltaic Panels",
          "PV Panels",
          "Solar Inverter",
          "Solar Inverters",
          "Solar Battery",
          "Solar Batteries",
          "Solar System",
          "Solar Systems",
          "Solar Installation",
          "Solar Equipment",
          "Solar Marketplace",
          "Solar Auction",
          "Solar RFQ",
          "Solar Quote",
          "Renewable Energy",
          "Clean Energy",
          "Solar Manufacturer",
          "Solar Distributor",
          "Solar EPC"
        ]
  })

  // WebPage structured data
  const getWebPageData = () => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": content.title,
    "description": content.description,
    "url": url,
    "inLanguage": langCode,
    "keywords": content.keywords,
    "isPartOf": {
      "@type": "WebSite",
      "name": "EvoltBay",
      "url": "https://evoltbay.com",
      "description": isSpanish
        ? "Plataforma líder de comercio electrónico de energía solar y sistema de cotización empresarial"
        : "Leading solar energy ecommerce platform and enterprise quotation system"
    }
  })

  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={langCode} />
      
      {/* Basic Meta Tags */}
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <meta name="keywords" content={content.keywords} />
      
      {/* Language and Locale */}
      <meta httpEquiv="content-language" content={langCode} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Alternate Language Links */}
      <link rel="alternate" hrefLang="en" href="https://evoltbay.com/coming-soon" />
      <link rel="alternate" hrefLang="es" href="https://evoltbay.com/coming-soon" />
      <link rel="alternate" hrefLang="x-default" href="https://evoltbay.com/coming-soon" />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={content.ogTitle} />
      <meta property="og:description" content={content.ogDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="EvoltBay" />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={isSpanish ? "en_US" : "es_US"} />
      <meta property="og:image" content={`${typeof window !== 'undefined' ? window.location.origin : 'https://evoltbay.com'}/icons/icon-512x512.png`} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Evoltbay" />
      <meta name="twitter:title" content={content.twitterTitle} />
      <meta name="twitter:description" content={content.twitterDescription} />
      <meta name="twitter:image" content={`${typeof window !== 'undefined' ? window.location.origin : 'https://evoltbay.com'}/icons/icon-512x512.png`} />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="EvoltBay" />
      <meta name="theme-color" content="#2563eb" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getOrganizationData(), null, 2)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(getWebPageData(), null, 2)}
      </script>
    </Helmet>
  )
}

export default ComingSoonSEO
